import React, { useContext, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import moment from 'moment';
import { useCookies } from 'react-cookie';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useImmer } from 'use-immer';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { ReservationEventPayloadStandard } from '@r6digital/rapidstor-types/types/Events/Reservation';
import { ShowPricesPayload } from '@r6digital/rapidstor-types/types/Events/ShowPrices';
import { BaseReservation, RapidStorReservationRequest } from '@r6digital/rapidstor-types/types/Reservation';

import {
    usePatchReservationMutation,
    usePostSaveQuoteMutation,
    usePostTrackEventMutation,
} from '@/api/rapidstor';
import ChevronLeft from '@/assets/icon-chevron-left.svg?react';
import IconMailSent from '@/assets/icon-mail-sent.svg?react';
import Logger from '@/utils/logging';
import {
    payLaterButtonText,
    saveQuoteButtonText,
    showPricesTitleText,
} from '@/utils/rapidstor';
import { createId } from '@paralleldrive/cuid2';

import store from '@/store';
import {
    ContactCardState,
    selectContactCard,
    selectContactData,
    selectGoogleClickId,
    selectReservationData,
    setContactCard,
    setContactData,
    setReservationData,
} from '@/store/bookingSlice';

import { ANIMATION_SPEED, contactVariants } from '@/features/rapidstor/animations';
import {
    reservationEvent,
    saveQuoteEvent,
    showPricesEvent,
} from '@/features/rapidstor/PostMessageEvents';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import 'react-datepicker/dist/react-datepicker.css';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import classes from './styles.module.scss';

import Button from '../button';
import Spinner from '../spinner';

export type FormType = 'quote' | 'reservation' | 'showPrices' | 'contactMe';

interface Props {
    location: Location;
}

export default function ContactCard({ location }: Props) {
    const [emailQuote] = usePostSaveQuoteMutation();
    const [patchReservation] = usePatchReservationMutation();
    const navigate = useNavigate();
    const { 
        handleMakeReservation,
    } = useContext(JourneyContext);
    const contactData = useSelector(selectContactData);
    const contactCardState = useSelector(selectContactCard);
    const reservationData = useSelector(selectReservationData);
    const gclid = useSelector(selectGoogleClickId);
    const [cookies, setCookie] = useCookies(['contactData', 'sessionId']);
    const [postTrackEvent] = usePostTrackEventMutation();
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const { t } = useTranslation();
    /**
     * UI Animation State
     */
    const [formLoading, setFormLoading] = useState<boolean>(false);

    const dateRange = useMemo(() => {
        if (location.rapidstor.reservationNumberOfDaysInAdvanceLimit) {
            const minDate = moment();
            const maxDate = moment().add(
                location.rapidstor.reservationNumberOfDaysInAdvanceLimit,
                'days'
            );
            return {
                min: minDate.toDate(),
                max: maxDate.toDate(),
            };
        }
    }, [location.rapidstor.reservationNumberOfDaysInAdvanceLimit]);

    const getDefaultNeededDate = (): Date => {
        const now = moment(
            contactData?.neededDate || cookies?.contactData?.neededDate || new Date()
        );
        const excludedDates = location.rapidstor.datesClosed
            .split(',')
            .map((d) => moment(d, 'DD-MM-YYYY'));

        if (excludedDates.find((d) => d.isSame(now, 'day'))) {
            let nextDate = now.clone().add(1, 'days');
            while (excludedDates.find((d) => d.isSame(nextDate, 'day'))) {
                nextDate = nextDate.add(1, 'days');
            }
            return nextDate.toDate();
        }

        return now.toDate();
    };

    const [neededDate, setNeededDate] = useState<Date>(getDefaultNeededDate());

    // This sessionId is provided as external data when submitting the enquiry or
    // reservation to RapidStor. This allows external systems to link the lead and
    // reservation since RapidStor doesn't have a nice way of doing this natively.
    if (!cookies.sessionId) {
        setCookie('sessionId', createId(), {
            path: window.location.pathname,
            maxAge: 60 * 60 * 24,
        });
    }

    /**
     * Contact Form State
     *
     * Prefills using contactData from state if available
     */
    const [form, updateForm] = useImmer<Record<string, string>>({
        location: location.locationCode,
        firstName: contactData ? contactData.firstName : '',
        lastName: contactData ? contactData.lastName : '',
        email: contactData ? contactData.email : '',
        phone: contactData ? contactData.phone : '',
    });
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);

    const update = (key: string, event: React.BaseSyntheticEvent) => {
        updateForm((draft) => {
            draft[key] = String(event.target.value);
            return draft;
        });
    };

    const CTA_TITLE = useMemo((): string => {
        switch (contactCardState.type) {
            case 'showPrices':
                return showPricesTitleText(location);
            case 'reservation':
                return payLaterButtonText(location);
            case 'quote':
                return saveQuoteButtonText(location);
            case 'contactMe':
            default:
                return t('contact_card:contactMe:title');
        }
    }, [contactCardState.type, location, t]);

    const CTA_BUTTON = useMemo((): string => {
        switch (contactCardState.type) {
            case 'showPrices':
                return t('contact_card:showPrices:submit_text');
            case 'reservation':
                return payLaterButtonText(location);
            case 'quote':
                return saveQuoteButtonText(location);
            case 'contactMe':
            default:
                return t('contact_card:contactMe:submit_text');
        }
    }, [contactCardState.type, location, t]);

    const Detail_1 = useMemo((): string => {
        switch (contactCardState.type) {
            case 'showPrices':
                return t('contact_card:showPrices:form_title');
            case 'reservation':
                return t('contact_card:reservation:title');
            case 'quote':
            default:
                return t('contact_card:quote:title');
        }
    }, [contactCardState.type, t]);

    const Detail_2 = useMemo((): string => {
        switch (contactCardState.type) {
            case 'showPrices':
                return t('contact_card:showPrices:subtitle');
            case 'reservation':
                return t('contact_card:reservation:subtitle');
            case 'quote':
            default:
                return t('contact_card:quote:subtitle');
        }
    }, [contactCardState.type, t]);

    const Success_1 = useMemo((): string => {
        switch (contactCardState.type) {
            case 'reservation':
                return t('contact_card:reservation:success');
            case 'quote':
            default:
                return t('contact_card:quote:success');
        }
    }, [contactCardState.type, t]);

    const Success_2 = useMemo((): string => {
        switch (contactCardState.type) {
            case 'reservation':
                return t('contact_card:reservation:success_detail');
            case 'quote':
            default:
                return t('contact_card:quote:success_detail');
        }
    }, [contactCardState.type, t]);
    
    const patchExistingReservation = async () => {
        if (!reservationData?.token) {
            return null;
        }

        return await patchReservation({
            token: reservationData.token,
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            payload: {
                tenant: {
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                },
                reservation: {
                    neededDate: neededDate.toISOString(),
                    enquiry: false,
                    deal: contactCardState.deal || reservationData.deal || null,
                },
                cart: {},
                charges: {}
            }
        }).unwrap();
    }

    const sendReservationToRapidstor = async (isShowPrices: boolean) => {
        if (!contactCardState.unitType) {
            Logger.info(`[sendReservationToRapidstor] Missing unitType`, location);
            return;
        }

        // `neededDate` is a required field in the API. Without that value, we cannot submit.
        if (!neededDate) {
            Logger.info(`[sendReservationToRapidstor] Missing neededDate`, location);
            return;
        }

        // isShowPrices replaces the 'enquiry' parameter
        const isEnquiry = isShowPrices
            ? location.rapidstor.showPricesBehaviour.createLead
            : false;
        const sendWebhookOnly = isShowPrices
            ? !location.rapidstor.showPricesBehaviour.sendEmail &&
            !location.rapidstor.showPricesBehaviour.createLead
            : false;

        Logger.info(
            `[sendReservationToRapidstor] POST ${isEnquiry ? 'Lead' : 'Reservation'
            } request`,
            location
        );
        let dealId = null;
        if (contactCardState.isDeal) {
            dealId = contactCardState.deal || reservationData?.dealObject?._id;
        }

        const payload: RapidStorReservationRequest = {
            locationCode: location.locationCode,
            payload: {
                customListing: true,
                deal: dealId,
                sourceUrl: window.location.href,
                enquiry: isShowPrices,
                neededDate: neededDate.toISOString(),
                // Convert from hour to minute offset
                timezoneOffset: location.timezoneOffset * -60,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                insuranceCoverageId: reservationData?.descriptor.defaultInsuranceCoverage,
                tenant: {
                    tenantId: null,
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                },
                unitType: contactCardState.unitType._id,
                webhookOnly: sendWebhookOnly,
                externalData: {
                    SessionID: cookies.sessionId,
                    reservationSource: window.rapidstor_settings.app_config?.journey?.agentDetails?.agentName,
                    reservationComment: window.rapidstor_settings.app_config?.journey?.agentDetails?.agentComment,
                },
                googleClickId: gclid ? gclid : undefined,
            },
        };

        if (window.rapidstor_settings.app_config.journey.agentDetails) {
            payload.payload.externalData = {
                ...payload.payload.externalData,
                reservationSource: window.rapidstor_settings.app_config.journey.agentDetails.agentName,
                reservationComment: window.rapidstor_settings.app_config.journey.agentDetails.agentComment,
            }
        }

        let result;
        if (reservationData?.descriptor) {
            result = await handleMakeReservation(payload, reservationData.descriptor._id);
            if (!result) {
                throw new Error('Failed to make reservation');
            }
            store.dispatch(setReservationData({
                unitType: contactCardState.unitType,
                descriptor: reservationData?.descriptor,
                token: result.token,
            }));
        }

        Logger.info(
            `[sendReservationToRapidstor] ${isEnquiry ? 'Lead' : 'Reservation'} Created`,
            location
        );

        return result;
    };

    const sendEmailQuoteToRapidstor = async () => {
        if (!contactCardState.unitType) {
            Logger.info(`[sendEmailQuoteToRapidstor] Missing unitType`, location);
            return;
        }

        if (!neededDate) {
            Logger.info(`[sendEmailQuoteToRapidstor] Missing neededDate`, location);
            return;
        }

        Logger.info(`[sendEmailQuoteToRapidstor] POST email quote request`, location);

        const result = await emailQuote({
            locationCode: location.locationCode,
            corpCode: location.corpCode,
            payload: {
                customListing: true,
                deal: contactCardState.deal || null,
                sourceUrl: window.location.href,
                enquiry: true,
                neededDate: neededDate.toISOString(),
                // Convert from hour to minute offset
                timezoneOffset: location.timezoneOffset * -60,
                tenant: {
                    tenantId: null,
                    firstName: form.firstName,
                    lastName: form.lastName,
                    email: form.email,
                    phone: form.phone,
                },
                unitType: contactCardState.unitType._id,
                webhookOnly: false,
                externalData: {
                    SessionID: cookies.sessionId,
                    reservationSource: window.rapidstor_settings.app_config?.journey?.agentDetails?.agentName,
                    reservationComment: window.rapidstor_settings.app_config?.journey?.agentDetails?.agentComment,
                },
            },
        }).unwrap();

        Logger.info(`[sendEmailQuoteToRapidstor] Email Quote Sent`, location);

        return result;
    };

    const handleShowPricesSubmit = async () => {
        const payload: ShowPricesPayload = {
            firstName: form.firstName,
            lastName: form.lastName,
            phone: form.phone,
            email: form.email,
            neededDate: location.rapidstor.showPricesShowNeeded ? neededDate : null,
            locationCode: location.locationCode,
            locationName: location.name,
            description: `${contactCardState.unitType?.width}x${contactCardState.unitType?.length} ${contactCardState.unitType?.typeName}`,
            selected_unit_size: `${contactCardState.unitType?.width}x${contactCardState.unitType?.length} ${location.rapidstor.areaSizeSymbol}`,
        }
        if (contactCardState.unitType) {
            payload.weekly_price = contactCardState.unitType.rates.discounted ? contactCardState.unitType.rates.discountedRate / 4 : contactCardState.unitType.rates.rate / 4;
            payload.discount = contactCardState.unitType.deal?.title || '';
            payload.unitType = {
                dcLength: contactCardState.unitType.length,
                dcPreferredRate: 0,
                dcWidth: contactCardState.unitType.width,
                hasAvailableUnits: contactCardState.unitType.totalAvailable > 0,
                hasComputedAvailable: contactCardState.unitType.hasComputedAvailable,
                iFloor: contactCardState.unitType.floor,
                sLocationCode: contactCardState.unitType.locationCode,
                sTypeName: contactCardState.unitType.typeName,
                unitTypeId: contactCardState.unitType._id,
            }
        }
        if (contactCardState.isDeal && contactCardState.unitType?.deal) {
            payload.deal = {
                ...contactCardState.unitType.deal,
                ConcessionID: contactCardState.unitType.deal.discountPlan.ConcessionID || null,
            };
        }
        showPricesEvent(payload);
        store.dispatch(
            setContactData({
                firstName: form.firstName,
                lastName: form.lastName,
                phone: form.phone,
                email: form.email,
                neededDate: location.rapidstor.showPricesShowNeeded
                    ? neededDate.toISOString()
                    : undefined,
            })
        );
        setCookie(
            'contactData',
            {
                ...form,
                neededDate: location.rapidstor.showPricesShowNeeded
                    ? neededDate.toISOString()
                    : undefined,
            },
            {
                path: window.location.pathname,
                maxAge: 60 * 60 * 24,
            }
        );
        sendQuoteUnlockTrackingEvent();
        try {
            await sendReservationToRapidstor(true);
        } catch (err) {
            Logger.error(
                `[handleShowPricesSubmit] Error submitting enquiry form: ${JSON.stringify(
                    err
                )}`,
                location
            );
            fadeOutAndClose();
            setFormLoading(false);
        }
        store.dispatch(
            setContactCard({
                animate: 'initial',
                type: 'contactMe',
                unitType: null,
                isDeal: false,
                deal: null,
            })
        );
        setFormLoading(false);
        navigate(`/book-unit`);
    };

    const handleReservationSubmit = async () => {
        const now = moment();
        const needDate = moment(neededDate);
        if (
            needDate.diff(now, 'days') <
            location.rapidstor.reservationNumberOfDaysInAdvanceChargeFee
        ) {
            Logger.info(
                `[handleReservationSubmit] Reservation date is before the limit, free reservation`,
                location
            );
            firebaseLogEvent('free_reservation_created', {});
            try {
                const result = await sendReservationToRapidstor(false);
                if (result) {
                    const eventPayload = convertReservationToEventPayload(
                        location,
                        result,
                        contactCardState
                    );

                    setSubmitted(true);
                    setFormLoading(false);
                    reservationEvent(eventPayload);
                }
            } catch (err) {
                Logger.error(
                    `[handleReservationSubmit] Error submitting free reservation form: ${JSON.stringify(
                        err
                    )}`,
                    location
                );
                setFormLoading(false);
                fadeOutAndClose();
            }
        } else {
            Logger.info(
                `[handleReservationSubmit] Reservation date not before the limit, paid reservation`,
                location
            );
            firebaseLogEvent('paid_reservation_created', {});
            let result: BaseReservation | undefined | null;
            try {
                if (reservationData?.token) {
                    result = await patchExistingReservation();
                } else {
                    result = await sendReservationToRapidstor(false);
                }
            } catch (err) {
                Logger.error(
                    `[handleReservationSubmit] Error submitting paid reservation form: ${JSON.stringify(
                        err
                    )}`,
                    location
                );
                setFormLoading(false);
            }
            if (result) {
                setFormLoading(false);
                fadeOutAndClose();
                navigate(`/reservation-with-fee/${result.token}`);
            } else {
                setFormLoading(false);
                setError(true);
            }
        }
    };

    const convertReservationToEventPayload = (
        location: Location,
        reservation: BaseReservation,
        contactCardState: ContactCardState
    ): ReservationEventPayloadStandard => {
        const unitType = reservation.unitType;

        const MONTHS_IN_YEAR = 12;
        const WEEKS_IN_YEAR = 52;

        return {
            deal_name: contactCardState.unitType?.deal?.title || null,
            quoted_rate_monthly: reservation.rates.rate,
            quoted_rate_weekly: parseFloat(
                ((reservation.rates.rate * MONTHS_IN_YEAR) / WEEKS_IN_YEAR).toFixed(2)
            ),
            reservation,
            site_info: {
                location_code: location.locationCode,
                site_token: location.token,
            },
            size: unitType.area,
            tenant: {
                tenantId: reservation.tenantId,
                firstName: reservation.firstName,
                lastName: reservation.lastName,
                email: reservation.email,
                phone: reservation.phone,
            },
            unit_id: unitType.unitToUse ? unitType.unitToUse.UnitID : (null as never),
            unit_length: unitType.length,
            unit_name: reservation.sUnitName,
            unit_rate: reservation.rates.rate,
            unit_width: unitType.width,
            value: reservation.rates.rate,
        };
    };

    const handleContactSubmit = async () => {
        let result: BaseReservation | undefined;
        try {
            result = await sendEmailQuoteToRapidstor();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            Logger.error(
                `[handleContactSubmit] Error submitting quote email form: ${JSON.stringify(
                    err
                )}`,
                location
            );
            setFormLoading(false);
            setError(err?.data?.error || err.message || JSON.stringify(err));
        }

        if (result) {
            Logger.info(`[handleContactSubmit] Save Quote Email Sent`, location);

            setSubmitted(true);
            setFormLoading(false);
            saveQuoteEvent(
                convertReservationToEventPayload(location, result, contactCardState)
            );
        }
    };

    const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!contactCardState.unitType) {
            if (contactCardState.type === 'contactMe') {
                setFormLoading(true);
                firebaseLogEvent('save_quote', {});
                handleContactSubmit();
            } else {
                Logger.info(`[submitForm] Missing unitType`, location);
                setFormLoading(false);
                return;
            }
        }
        if (contactCardState.type === 'showPrices') {
            setFormLoading(true);
            firebaseLogEvent('show_prices', {});
            handleShowPricesSubmit();
        } else if (contactCardState.type === 'reservation') {
            setFormLoading(true);
            handleReservationSubmit();
        } else {
            setFormLoading(true);
            firebaseLogEvent('save_quote', {});
            handleContactSubmit();
        }
    };

    const sendQuoteUnlockTrackingEvent = () => {
        try {
            postTrackEvent({
                locationToken: window.rapidstor_settings.rapidstor_options.locationToken,
                corpToken: window.rapidstor_settings.rapidstor_options.corpToken,
                payload: {
                    trackingId: createId(),
                    url: window.location.href,
                    referrerUrl: document.referrer,
                    userAgent: window.navigator.userAgent,
                    action: 'email_user_quoted',
                    timestamp: new Date().toISOString(),
                },
            });
        } catch (error) {
            Logger.warn(
                `[sendQuoteUnlockTrackingEvent] Error thrown in POST track event request: ${JSON.stringify(
                    error
                )}`,
                location
            );
        }
    };

    const fadeOutAndClose = () => {
        if (error) {
            setError(false);
        }
        store.dispatch(
            setContactCard({
                animate: 'fadeOut',
                type: contactCardState.type,
                unitType: null,
            })
        );
        
        setTimeout(() => {
            store.dispatch(
                setContactCard({
                    animate: 'initial',
                    type: contactCardState.type,
                    unitType: null,
                })
            );
        }, 300);
    };

    return (
        <div id="contact-card-modal" style={{
            zIndex: 50000,
            position: 'fixed',
        }}>
            <motion.div
                layout={true}
                variants={contactVariants[0]}
                initial='initial'
                animate={contactCardState.animate}
                transition={{
                    duration: ANIMATION_SPEED,
                }}
                className={classes.backdrop}
            >
                <motion.div
                    layout={true}
                    variants={contactVariants[1]}
                    initial='initial'
                    animate={contactCardState.animate}
                    transition={{
                        duration: ANIMATION_SPEED,
                    }}
                    className={classes.card}
                >
                    <div
                        className={classes.header}
                        style={{
                            display:
                                contactCardState.animate === 'initial' ? 'none' : 'flex',
                        }}
                    >
                        <button onClick={() => {
                            setSubmitted(false);
                            fadeOutAndClose();
                        }} className={classes.back_btn}>
                            <ChevronLeft />
                        </button>
                        <span className={`${classes.cta}`}>{CTA_TITLE}</span>

                        <button onClick={() => {
                            setSubmitted(false);
                            fadeOutAndClose();
                        }} className={classes.cancel_btn}>
                            {t('cta:cancel')}
                        </button>
                    </div>

                    <div className={`${classes.confirmation} ${!error && classes.hide}`}>
                        <span className={`${classes.header}`}>{t('contact_card:error')}</span>
                        { error && typeof error === 'string' && <p>{error}</p> }
                        { error && typeof error !== 'string' && <p>{t('contact_card:error_text')}</p> }
                        <p>
                            {t('contact_card:phone')}: {location.contactPhone}
                        </p>
                        <p>
                            {t('contact_card:email')}: {location.corpCode === 'CRUK' ? t('contact_card:emailOverride') : location.contactEmail}
                        </p>
                        <Button
                            onclick={() => {
                                setSubmitted(false);
                                fadeOutAndClose();
                            }}
                            type='primary'
                            size='medium'
                        >
                            {t('cta:close')}
                        </Button>
                    </div>

                    <div
                        className={`${classes.copy}  ${(submitted || error) && classes.hide}`}
                        style={{
                            display:
                                contactCardState.animate === 'initial' ? 'none' : 'block',
                        }}
                    >
                        <div className={classes.top}>
                            <span className={classes.detail_1}>{Detail_1}</span>
                        </div>
                        <p className={classes.detail_2}>{Detail_2}</p>
                    </div>

                    <div
                        className={`${classes.confirmation}  ${!submitted && classes.hide}`}
                        style={{
                            display:
                                contactCardState.animate === 'initial' ? 'none' : 'block',
                        }}
                    >
                        <div className={`${classes.container}`}>
                            <IconMailSent />
                            <span className={`${classes.success_1}`}>{Success_1}</span>
                            <p className={`${classes.success_2}`}>{Success_2}</p>
                        </div>

                        <Button
                            onclick={() => {
                                setSubmitted(false);
                                fadeOutAndClose();
                            }}
                            type='primary'
                            size='medium'
                        >
                            {t('cta:close')}
                        </Button>
                    </div>

                    <form
                        className={`${classes.form} ${(submitted || error) && classes.hide}`}
                        onSubmit={submitForm}
                        style={{
                            display:
                                contactCardState.animate === 'initial' ? 'none' : 'block',
                        }}
                    >
                        <div className={classes.part}>
                            <label className={classes.label}>{t('contact_card:first_name')}</label>
                            <input
                                type='text'
                                autoComplete='fName'
                                name='firstName'
                                value={form.firstName}
                                onChange={(e) => update('firstName', e)}
                                disabled={formLoading}
                                required
                                className={classes.input}
                            />
                        </div>
                        <div className={classes.part}>
                            <label className={classes.label}>{t('contact_card:last_name')}</label>
                            <input
                                type='text'
                                autoComplete='lName'
                                name='lastName'
                                value={form.lastName}
                                onChange={(e) => update('lastName', e)}
                                disabled={formLoading}
                                required
                                className={classes.input}
                            />
                        </div>
                        <div className={classes.part}>
                            <label className={classes.label}>{t('contact_card:email_address')}</label>
                            <input
                                type='email'
                                autoComplete='email'
                                name='email'
                                value={form.email}
                                onChange={(e) => update('email', e)}
                                disabled={formLoading}
                                required
                                className={classes.input}
                            />
                        </div>
                        {(location.rapidstor.showPricesShowNeeded ||
                            contactCardState.type === 'reservation') && (
                                <div className={`${classes.part}`}>
                                    <label className={classes.label}>{t('contact_card:move_in_date')}</label>
                                    <DatePicker
                                        showIcon
                                        toggleCalendarOnIconClick={true}
                                        selected={neededDate}
                                        minDate={dateRange?.min}
                                        maxDate={dateRange?.max}
                                        required
                                        onFocus={(e) => {
                                            e.currentTarget.blur();
                                        }}
                                        excludeDates={location.rapidstor.datesClosed
                                            .split(',')
                                            .map((d) => {
                                                return moment(d, 'DD-MM-YYYY').toDate();
                                            })}
                                        disabledKeyboardNavigation={true}
                                        dateFormat={location.rapidstor.dateFormat}
                                        onChange={(e: Date | null) => {
                                            if (e) {
                                                setNeededDate(e);
                                            }
                                        }}
                                        className={`${classes.datepicker} ${classes.input}`}
                                        disabled={formLoading}
                                    />
                                </div>
                            )}
                        <div className={classes.part}>
                            <label className={classes.label}>{t('contact_card:phone_number')}</label>
                            <input
                                type='tel'
                                autoComplete='tel'
                                name='phone'
                                value={form.phone}
                                onChange={(e) => update('phone', e)}
                                disabled={formLoading}
                                className={classes.input}
                                required
                            />
                        </div>
                        <div className={classes.part}>
                            <Button disabled={formLoading} type='primary' size='medium'>
                                {formLoading ? <Spinner /> : CTA_BUTTON}
                            </Button>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </div >
    );
}
